import { getCwUrl, getGBUrl, getIamUrl } from '../utils/config';
import { fetchFn, getFetch, postFetch, putFetch } from '../utils/fetch';
import { useQuery } from '@tanstack/react-query';

export const getAssignmentCompletion = async (courseId, assignmentIds = []) => {
  try {
    const gradebookV1Url = `${getGBUrl()}/api/v1`;

    const completionCountV2 = `${gradebookV1Url}/courses/${courseId}/assignments/aggregate-completion-statuses`;
    const response = await postFetch(completionCountV2, { assignmentIds });
    if (response.ok) {
      const data = await response.json();
      return data.assignmentCompletionStatuses;
    }
    return [];
  } catch (_error) {
    return [];
  }
};

export const getGradebookComments = async (courseId, body) => {
  const gradebookV1Url = `${getGBUrl()}/api/v1`;
  const endPoint = `${gradebookV1Url}/courses/${courseId}/gradebook/comments`;
  const response = await postFetch(endPoint, body);
  if (!response.ok) {
    throw new Error(
      `Server request to get gradebook comments failed.  ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

export const putGradebookComment = async (payload) => {
  const { courseId } = payload;
  const gradebookV1Url = `${getGBUrl()}/api/v1`;
  const endPoint = `${gradebookV1Url}/courses/${courseId}/gradebook/comments`;
  const response = await putFetch(endPoint, payload);
  if (!response.ok) {
    throw new Error(
      `Server request to update gradebook comment failed.  ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

export const getGradebookSettings = async (courseId) => {
  const gradebookV1Url = `${getGBUrl()}/api/v1`;
  const endPoint = `${gradebookV1Url}/courses/${courseId}/gradebook/settings`;
  const response = await getFetch(endPoint);
  if (!response.ok) {
    throw new Error(
      `Server request to get gradebook settings failed.  ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

export const getAssignmentCompletionStatuses = async (courseId) => {
  const gradebookV1Url = `${getGBUrl()}/api/v1`;
  const endPoint = `${gradebookV1Url}/courses/${courseId}/assignments/completion-statuses`;
  const response = await getFetch(endPoint);
  if (!response.ok) {
    throw new Error(
      `Server request to get gradebook completions failed.  ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

export const getGradebookV2 = async (courseId, body) => {
  const gradebookV1Url = `${getGBUrl()}/api/v1`;
  const endPoint = `${gradebookV1Url}/courses/${courseId}/gradebook/grades`;
  const response = await postFetch(endPoint, body);
  if (!response.ok) {
    throw new Error(
      `Server request to get grades failed.  ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

export const getGradebookAverages = async (
  courseId,
  { assignmentIds, userIds },
) => {
  const gradebookV1Url = `${getGBUrl()}/api/v1`;
  const endPoint = `${gradebookV1Url}/courses/${courseId}/gradebook/averages`;
  const response = await postFetch(endPoint, { assignmentIds, userIds });
  if (!response.ok) {
    throw new Error(
      `Server request to get gradebook averages failed.  ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

export const downloadGradebookExportV2 = async (courseId, options) => {
  const gradebookV2Url = `${getGBUrl()}/api/v2`;
  const endPoint = `${gradebookV2Url}/courses/${courseId}/gradebook/export`;
  const response = await postFetch(endPoint, options);
  if (!response.ok) {
    throw new Error(
      `Server request to get gradebook export failed.  ${response.status} ${response.statusText}`,
    );
  }
  return response.blob();
};

export const updateGradebookSettings = async (courseId, gradebookSettings) => {
  const gradebookV1Url = `${getGBUrl()}/api/v1`;
  const endPoint = `${gradebookV1Url}/courses/${courseId}/gradebook/settings`;
  const options = {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ gradebookSettings }),
  };
  const response = await fetchFn(endPoint, options);
  if (!response.ok) {
    throw new Error(
      `Server request to update gradebook settings failed.  ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

export const updateGradebookCategories = async (
  courseId,
  gradebookCategories,
  isGradingTypeStandard,
) => {
  const gradebookV1Url = `${getGBUrl()}/api/v1`;
  const endPoint = `${gradebookV1Url}/courses/${courseId}/gradebook/categories`;
  const options = {
    method: 'PUT',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ gradebookCategories, isGradingTypeStandard }),
  };
  const response = await fetchFn(endPoint, options);
  if (!response.ok) {
    throw new Error(
      `Server request to update gradebook categories failed.  ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

export const getStudentIdsSortedByAssignmentGrade = async (
  courseId,
  assignmentId,
) => {
  const gradebookV1Url = `${getGBUrl()}/api/v1`;
  const endPoint = `${gradebookV1Url}/courses/${courseId}/assignments/${assignmentId}/students-sorted-by-grade`;
  const response = await getFetch(endPoint);
  if (!response.ok) {
    throw new Error(
      `Server request to get student by assignment grade failed.  ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

export const overrideGrade = async (payload) => {
  const { courseId } = payload;
  const gradebookV1Url = `${getGBUrl()}/api/v1`;
  const endPoint = `${gradebookV1Url}/courses/${courseId}/gradebook/override/grades`;
  const response = await postFetch(endPoint, payload);
  if (!response.ok) {
    throw new Error(
      `Server request to override grade failed.  ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

export const getGradebook = async (courseId, body) => {
  const gradebookV2Url = `${getGBUrl()}/api/v2`;
  const endPoint = `${gradebookV2Url}/courses/${courseId}/gradebook/grades`;
  const response = await postFetch(endPoint, body);
  if (!response.ok) {
    throw new Error(
      `Server request to get grades failed.  ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

export const getIndividualStudentGradebook = async (courseId, studentId) => {
  const gradebookV2Url = `${getGBUrl()}/api/v2`;
  const endPoint = `${gradebookV2Url}/courses/${courseId}/users/${studentId}/grades`;
  const response = await getFetch(endPoint);
  if (!response.ok) {
    throw new Error(
      `Server request to get grades failed.  ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

/**
 * Get instructor gradebook with server-side pagination, search, filtering, and sorting.
 * Consolidates multiple API calls into a single optimized endpoint.
 * @param {string} courseId - The course UUID
 * @param {Object} options - Request options
 * @param {number} [options.page=1] - Page number for pagination
 * @param {number} [options.pageSize=25] - Number of students per page
 * @param {string} [options.search] - Search term to filter by name/email
 * @param {string} [options.sortBy='name'] - Field to sort by ('name' or 'course_total')
 * @param {string} [options.sortByAssignment] - Assignment UUID to sort by assignment grade
 * @param {number} [options.sortByCategory] - Category ID to sort by category total
 * @param {string} [options.sortDirection='asc'] - Sort direction ('asc' or 'desc')
 * @param {boolean} [options.sortByPoints=false] - Sort by points instead of percentage
 * @param {string[]} [options.exceptionGroupIds] - Filter to students in specific groups
 * @param {boolean} [options.includeDroppedStudents=false] - Include dropped students
 * @param {string} [options.clientTimezone] - IANA timezone for due-date filtering
 * @returns {Promise<Object>} Paginated gradebook data
 */
export const getInstructorGradebook = async (courseId, options = {}) => {
  const gradebookV1Url = `${getGBUrl()}/api/v1`;
  const endPoint = `${gradebookV1Url}/courses/${courseId}/gradebook/instructor`;
  const response = await postFetch(endPoint, options);
  if (!response.ok) {
    throw new Error(
      `Server request to get instructor gradebook failed.  ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

export const getCurrentUserAndPermissions = async (courseId) => {
  const gradebookV1Url = `${getGBUrl()}/api/v1`;
  const endPoint = `${gradebookV1Url}/courses/${courseId}/user`;
  const response = await getFetch(endPoint);
  if (!response.ok) {
    throw new Error(
      `Server request to get user permissions failed.  ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

export const getCourseRoster = async (courseId) => {
  const gradebookV1Url = `${getGBUrl()}/api/v1`;
  const response = await getFetch(
    `${gradebookV1Url}/courses/${courseId}/roster`,
  );
  if (!response.ok) {
    throw new Error(
      `Server request to get roster failed.  ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

export const getExceptionGroups = async (courseId) => {
  const gradebookV1Url = `${getGBUrl()}/api/v1`;
  const response = await getFetch(
    `${gradebookV1Url}/groups/courses/${courseId}/exception-groups`,
  );
  if (!response.ok) {
    throw new Error(
      `Server request to get exception groups failed.  ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

export const useExceptionGroupsQuery = (id, removeUngrouped = false) =>
  useQuery({
    queryFn: ({ queryKey: [_, courseId] }) => getExceptionGroups(courseId),
    queryKey: ['exceptionGroups', id],
    initialData: { groups: [] },
    select: (results) => {
      let groups = [...results.groups];
      if (removeUngrouped) {
        groups = groups.filter((group) => group.groupId !== 'ungrouped');
      }
      return groups.map((group) => {
        return {
          groupId: group.groupId,
          label: group.name,
          name: group.name,
          studentIds: group.students,
        };
      });
    },
  });

export const setUserLmsId = async (info) => {
  const baseUrl = `${getCwUrl()}/lms`;
  const response = await postFetch(`${baseUrl}/user/profile`, info);
  if (!response.ok) {
    throw new Error(
      `Server request to set users third party lms id failed.  ${response.status} ${response.statusText}`,
    );
  }
  const data = await response.json();
  data.thirdPartyUserId = info.value;
  return data;
};

export const extendSession = async () => {
  const response = await fetchFn(`${getIamUrl()}/v2/client/token/renewal`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Pragma: 'no-cache',
    },
  });

  if (response.ok) return response.text();

  throw await response.text();
};
